/**
 * NOLAS 2026, application multi evenement e.labriquerie.net.
 * src/i18n/fr.cvConseils.ts
 *
 * Messages de la page « Mes conseils » (/mes-conseils) et de l'encart qui y
 * mène depuis le dépôt du CV. Fusionné dans fr.ts.
 *
 * Le serveur (api/me/cv-conseils.php) ne renvoie que des CODES et des
 * paramètres : toutes les phrases sont ici, une par code. Un code inconnu du
 * catalogue n'est jamais affiché brut (voir mesConseilsModel.ts).
 *
 * Plateforme multi-événements : aucun nom d'événement, de lieu, ni « salon »,
 * « atelier », « exposant » écrits en dur. Le vocabulaire vient des jetons
 * {lexExhibitor...}, {lexWorkshop...} et {eventType...}, substitués au-dessus
 * de react-intl. Les accords (cet atelier / cette activité) passent par les
 * formes démonstratives et négatives des jetons, jamais par un article écrit
 * à la main.
 *
 * Mikael - chef de projet - 2026-10-04
 */
export const messagesCvConseilsFr: Record<string, string> = {
  // ── Page ───────────────────────────────────────────────────────────────
  'cvConseils.retour': 'Retour',
  'cvConseils.titre': 'Mes conseils',
  'cvConseils.sousTitre':
    'Ce que votre CV peut gagner, les {lexExhibitorPlural} à rencontrer et les {lexWorkshopPlural} à suivre, avec à chaque fois la raison.',
  'cvConseils.chargement': 'Chargement de vos conseils…',
  'cvConseils.erreur': 'Impossible de charger vos conseils pour le moment.',
  'cvConseils.erreur.reessayer': 'Réessayer',
  'cvConseils.erreur.nonCandidat':
    'Cette page est réservée aux personnes qui déposent un CV.',
  'cvConseils.methode':
    'Comment ces propositions sont faites : elles sont calculées à partir de votre CV et de votre profil, par des règles fixes, et chacune affiche sa raison. Quand elle est disponible, une intelligence artificielle peut ensuite personnaliser les explications : elle ne choisit et n’ajoute aucune proposition.',

  // ── États ──────────────────────────────────────────────────────────────
  'cvConseils.etat.sans_cv.titre': 'Déposez votre CV pour recevoir vos conseils',
  'cvConseils.etat.sans_cv.texte':
    'Une fois votre CV déposé, nous vous dirons ce qu’il peut gagner et {lexExhibitorDefPlural} qu’il peut intéresser. En attendant, voici des pistes tirées de votre profil.',
  'cvConseils.etat.sans_cv.lien': 'Déposer mon CV',
  'cvConseils.etat.non_indexe.titre': 'Votre CV n’a pas encore été lu',
  'cvConseils.etat.non_indexe.texte':
    'Nous n’avons pas pu lire votre CV. S’il s’agit d’un scan ou d’une photo, déposez plutôt un PDF enregistré depuis votre traitement de texte. En attendant, voici des pistes tirées de votre profil.',
  'cvConseils.etat.non_indexe.lien': 'Déposer à nouveau mon CV',
  'cvConseils.etat.mineur_depot_ferme.titre': 'Dépôt de CV fermé aux mineurs',
  'cvConseils.etat.mineur_depot_ferme.texte':
    'Pour {eventTypeDef}, le dépôt de CV n’est pas ouvert aux personnes mineures. Voici tout de même des pistes tirées de votre profil.',
  'cvConseils.etat.cv_desactive.titre': 'Dépôt de CV indisponible',
  'cvConseils.etat.cv_desactive.texte':
    'Le dépôt de CV n’est pas proposé pour {eventTypeDef}. Voici des pistes tirées de votre profil.',

  'cvConseils.profil.incomplet':
    'Précisez votre secteur et le contrat recherché dans votre profil : les propositions seront plus justes.',
  'cvConseils.profil.lien': 'Compléter mon profil',

  // ── Ce que nous avons lu ───────────────────────────────────────────────
  'cvConseils.lecture.titre': 'Ce que nous avons lu dans votre CV',
  'cvConseils.lecture.mots': '{mots, plural, one {# mot lu} other {# mots lus}}',
  'cvConseils.lecture.motsCles': 'Mots-clés repérés : {liste}',
  'cvConseils.lecture.langues': 'Langues repérées : {liste}',
  'cvConseils.lecture.secteur': 'Votre CV évoque le secteur : {secteur}',

  // ── Conseils d'amélioration du CV ──────────────────────────────────────
  'cvConseils.conseils.titre': 'Pour améliorer votre CV',
  'cvConseils.conseils.aucun':
    'Votre CV contient l’essentiel : coordonnées, expériences, formation et compétences. Bravo !',
  'cvConseils.conseils.remplacer': 'Remplacer mon CV',
  'cvConseils.priorite.haute': 'Prioritaire',
  'cvConseils.priorite.moyenne': 'Important',
  'cvConseils.priorite.basse': 'Conseil',

  'cvConseils.conseil.illisible.titre': 'Votre CV n’a pas pu être lu',
  'cvConseils.conseil.illisible.texte':
    'Le fichier ne contient presque pas de texte lisible : c’est souvent un scan ou une photo. Les outils des recruteurs lisent le texte, pas l’image. Enregistrez votre CV en PDF depuis votre traitement de texte (fonction « Exporter en PDF »), puis déposez-le à nouveau.',
  'cvConseils.conseil.lecture_partielle.titre': 'Une partie de votre CV n’a pas pu être lue',
  'cvConseils.conseil.lecture_partielle.texte':
    'Certaines parties du fichier sont des images ou un format inhabituel : ce qu’elles contiennent risque d’échapper aux recruteurs. Enregistrez à nouveau votre CV en PDF depuis votre traitement de texte.',
  'cvConseils.conseil.trop_court.titre': 'Votre CV est un peu court',
  'cvConseils.conseil.trop_court.texte':
    'Nous y avons lu {mots} mots. Détaillez vos expériences : ce que vous faisiez, avec quels outils, pour quels résultats. Un recruteur doit pouvoir imaginer ce que vous savez faire.',
  'cvConseils.conseil.trop_long.titre': 'Votre CV est long',
  'cvConseils.conseil.trop_long.texte':
    'Nous y avons lu {mots} mots. Un recruteur parcourt un CV en moins d’une minute : visez une ou deux pages, en gardant les expériences les plus proches du poste visé.',
  'cvConseils.conseil.sans_courriel.titre': 'Ajoutez votre adresse e-mail',
  'cvConseils.conseil.sans_courriel.texte':
    'Nous n’avons trouvé aucune adresse e-mail dans votre CV. Sans elle, une entreprise intéressée ne peut pas vous recontacter. Placez-la en haut, avec votre téléphone.',
  'cvConseils.conseil.sans_telephone.titre': 'Ajoutez votre numéro de téléphone',
  'cvConseils.conseil.sans_telephone.texte':
    'Nous n’avons trouvé aucun numéro de téléphone dans votre CV. C’est le moyen le plus rapide pour un recruteur de vous proposer un entretien.',
  'cvConseils.conseil.rubrique_experience.titre': 'Ajoutez une rubrique « Expériences »',
  'cvConseils.conseil.rubrique_experience.texte':
    'Nous n’avons pas repéré de rubrique d’expériences. Pour chaque poste, indiquez les dates, l’entreprise et vos principales missions.',
  'cvConseils.conseil.rubrique_experience_debutant.titre': 'Montrez ce que vous avez déjà fait',
  'cvConseils.conseil.rubrique_experience_debutant.texte':
    'Pas encore d’expérience professionnelle ? Stages, jobs d’été, garde d’enfants, bénévolat, projets scolaires, engagement associatif ou sportif : tout cela compte. Regroupez-les sous une rubrique « Expériences ».',
  'cvConseils.conseil.rubrique_formation.titre': 'Ajoutez votre formation',
  'cvConseils.conseil.rubrique_formation.texte':
    'Nous n’avons pas repéré de rubrique formation. Indiquez vos diplômes, ou la formation en cours, avec l’établissement et l’année.',
  'cvConseils.conseil.rubrique_competences.titre': 'Listez vos compétences',
  'cvConseils.conseil.rubrique_competences.texte':
    'Une rubrique « Compétences » montre en un coup d’œil ce que vous savez faire : outils, machines, logiciels, savoir-faire, qualités.',
  'cvConseils.conseil.rubrique_langues.titre': 'Indiquez les langues que vous parlez',
  'cvConseils.conseil.rubrique_langues.texte':
    'Même un niveau scolaire compte. Précisez-le pour chaque langue : notions, intermédiaire, courant.',
  'cvConseils.conseil.sans_accroche.titre': 'Dites en une ligne ce que vous cherchez',
  'cvConseils.conseil.sans_accroche.texte':
    'En haut du CV, une phrase suffit : le poste ou le contrat visé, et ce que vous apportez. Le recruteur sait tout de suite pourquoi il lit votre CV.{recherche, select, alternance { Par exemple : « Recherche une alternance en … à partir de … ».} stage { Par exemple : « Recherche un stage de … semaines en … ».} emploi { Par exemple : « Technicien de maintenance, 5 ans d’expérience, disponible immédiatement ».} other {}}',
  'cvConseils.conseil.sans_dates.titre': 'Datez votre parcours',
  'cvConseils.conseil.sans_dates.texte':
    'Nous n’avons trouvé aucune année dans votre CV. Indiquez les dates de vos expériences et de vos formations, par exemple « 2022 - 2024 » : un parcours sans dates se lit mal.',
  'cvConseils.conseil.mobilite.titre': 'Précisez votre mobilité',
  'cvConseils.conseil.mobilite.texte':
    'Si vous avez le permis B ou un véhicule, écrivez-le : pour beaucoup de postes, c’est un critère décisif. Sinon, indiquez les trajets que vous pouvez faire.',
  'cvConseils.conseil.mineur_adresse.titre': 'Votre ville suffit',
  'cvConseils.conseil.mineur_adresse.texte':
    'Votre CV contient votre adresse complète. Pour votre sécurité, indiquez seulement votre ville : les recruteurs vous contacteront par e-mail ou par téléphone.',
  'cvConseils.conseil.date_naissance.titre': 'La date de naissance n’est pas utile',
  'cvConseils.conseil.date_naissance.texte':
    'Un recruteur n’a pas besoin de votre date de naissance pour évaluer vos compétences. Vous pouvez la retirer.',
  'cvConseils.conseil.mots_offres.titre': 'Des mots des offres qui vous correspondent manquent à votre CV',
  'cvConseils.conseil.mots_offres.texte':
    'Les offres proches de votre profil emploient ces mots, absents de votre CV : {mots}. Ajoutez-les seulement s’ils correspondent à une compétence ou à une expérience réelle : un recruteur vous interrogera dessus.',

  // ── Exposants à rencontrer ─────────────────────────────────────────────
  'cvConseils.exposants.titre': '{lexExhibitorPluralCap} à rencontrer',
  'cvConseils.exposants.intro':
    'Du plus pertinent au moins pertinent, avec ce qui nous a fait les choisir.',
  'cvConseils.exposants.aucun':
    '{lexExhibitorNoneCap} ne correspond clairement à votre profil pour l’instant. Complétez votre profil ou parcourez la liste complète.',
  'cvConseils.exposants.voirTous': 'Voir {lexExhibitorAllDefPlural}',
  'cvConseils.exposants.fiche': 'Voir la fiche',
  'cvConseils.exposants.fiche.aria': 'Voir la fiche de {nom}',
  'cvConseils.exposants.postes': 'Offres qui vous correspondent',
  'cvConseils.exposants.poste.places': '{places, plural, one {# poste} other {# postes}}',

  'cvConseils.raison.mots_cv': 'Votre CV mentionne {mots}.',
  'cvConseils.raison.secteur': 'C’est le secteur que vous recherchez : {secteur}.',
  'cvConseils.raison.secteur_cv': 'Votre CV relève du secteur {secteur}.',
  'cvConseils.raison.secteur_proche': 'Secteur proche du vôtre : {secteur}.',
  'cvConseils.raison.postes':
    '{lexExhibitorDemCap} propose {places, plural, one {# poste} other {# postes}} de « {titre} »{contrat}{autres, plural, =0 {} one {, et # autre offre qui vous correspond} other {, et # autres offres qui vous correspondent}}.',
  'cvConseils.raison.contrat': 'Le contrat proposé correspond à votre recherche : {contrat}.',
  'cvConseils.raison.niveau': 'Le niveau d’expérience demandé est proche du vôtre.',

  // Contrats (valeurs exactes de la base) et la tournure « en CDI ».
  'cvConseils.contrat.en': ' en {contrat}',
  'cvConseils.contrat.CDI': 'CDI',
  'cvConseils.contrat.CDD': 'CDD',
  'cvConseils.contrat.Alternance': 'alternance',
  'cvConseils.contrat.Apprentissage': 'apprentissage',
  'cvConseils.contrat.Stage': 'stage',
  'cvConseils.contrat.Interim': 'intérim',
  'cvConseils.contrat.Saisonnier': 'contrat saisonnier',

  // ── Ateliers à suivre ──────────────────────────────────────────────────
  'cvConseils.ateliers.titre': '{lexWorkshopPluralCap} à suivre',
  'cvConseils.ateliers.intro': 'Une sélection faite pour votre profil, avec les places encore disponibles.',
  'cvConseils.ateliers.aucun':
    'Rien ne ressort clairement pour votre profil pour l’instant. Le programme complet reste à votre disposition.',
  'cvConseils.ateliers.programme': 'Voir le programme',
  'cvConseils.ateliers.horaire': 'De {debut} à {fin}',
  'cvConseils.ateliers.salle': 'Lieu : {salle}',
  'cvConseils.ateliers.places':
    '{places, plural, =0 {Complet} one {# place restante} other {# places restantes}}',
  'cvConseils.ateliers.libre': 'Accès libre',
  'cvConseils.ateliers.creneaux': 'Créneaux encore ouverts : {liste}',
  'cvConseils.ateliers.dejaInscrit': 'Inscription déjà enregistrée',
  'cvConseils.ateliers.lien': 'Voir et s’inscrire',
  'cvConseils.ateliers.lien.aria': 'Voir {titre} et s’inscrire',

  'cvConseils.atelier.raison.sans_cv':
    'Vous n’avez pas encore déposé de CV : {lexWorkshopDem} vous aide à le construire.',
  'cvConseils.atelier.raison.cv_a_ameliorer':
    'Votre CV a {nombre, plural, one {# point} other {# points}} à améliorer : {lexWorkshopDem} vous aide à le reprendre.',
  'cvConseils.atelier.raison.sans_experience':
    'Peu ou pas d’expérience déclarée : vous y apprendrez à présenter votre parcours et à réussir un entretien.',
  'cvConseils.atelier.raison.recherche_emploi':
    'Vous cherchez un emploi : préparez vos entretiens avec des recruteurs.',
  'cvConseils.atelier.raison.reconversion':
    'Vous êtes en reconversion : changer de métier, faire reconnaître vos acquis, vous former.',
  'cvConseils.atelier.raison.alternance':
    'Vous cherchez une alternance ou une formation : contrats, écoles et démarches.',
  'cvConseils.atelier.raison.stage': 'Vous cherchez un stage : comment le trouver et le décrocher.',
  'cvConseils.atelier.raison.formation':
    'Vous voulez vous former : financements et parcours qualifiants.',
  'cvConseils.atelier.raison.orientation': 'Pour découvrir des métiers et préciser votre projet.',
  'cvConseils.atelier.raison.numerique':
    'Pour soigner votre présence en ligne, que les recruteurs consultent.',
  'cvConseils.atelier.raison.secteur': 'En lien avec le secteur {secteur}.',

  // ── Enrichissement IA (facultatif : la page est complète sans lui) ──────
  'cvConseils.ia.enCours': 'Personnalisation de vos conseils en cours…',
  'cvConseils.ia.regles': 'Conseils établis par nos règles.',
  'cvConseils.ia.mention':
    'Suggestions personnalisées produites automatiquement à partir de votre CV : vérifiez-les avant de modifier votre CV.',
  'cvConseils.ia.consentement': 'Activer les suggestions personnalisées',
  'cvConseils.ia.synthese.titre': 'En résumé',
  'cvConseils.ia.conseils.titre': 'Suggestions personnalisées',
  'cvConseils.ia.pourquoi': 'Selon votre CV : {texte}',

  // ── Encart après le dépôt du CV, et lien de la page « Plus » ───────────
  'cvConseils.encart.titre': 'Votre CV est déposé. Et maintenant ?',
  'cvConseils.encart.texte':
    'Découvrez comment l’améliorer, puis {lexExhibitorDefPlural} à aller voir et {lexWorkshopDefPlural} à suivre, avec la raison de chaque proposition.',
  'cvConseils.encart.lien': 'Voir mes conseils',
  'cvConseils.plus.lien': 'Mes conseils',
};
